import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  CommonBackendHelper,
  DateHelper,
  DynamicTableHelper,
  FileHelper,
  TooltipWrapper,
  useTableHook,
} from "@workspace/common";
import {
  REPORT_MANDATORY_OPTIONS,
  PREOFFER_INITIAL_OPTIONS,
  SEARCH_FIELDS,
  BGC_STATUS,
  BGC_SUB_STATUS,
} from "./ELCTableConstants.js";
import "./ELCTable.scss";
import "./DynamicTableWrapper.jsx";
import DynamicTableWrapper from "./DynamicTableWrapper.jsx";
import ELCForms from "../ELCForms/ELCForms.jsx";
import ELCModal from "../ELCModal/ELCModal.jsx";
import { toast } from "react-toastify";
import {
  fetchBGCCounts,
  fetchJobForm,
  fetchJobFormReset,
  fetchPreOfferCandidates,
  fetchPreOfferCounts,
  fetchSLACounts,
  resetChangeStatus,
  resetOfferStatus,
  resetOfferWithAttachmentsStatus,
  tagReset,
} from "@workspace/job/src/store/actions.js";
import { Link } from "react-router-dom";
import OfferPreviewModal from "../OfferPreviewModal/OfferPreviewModal.jsx";
import { isBackDate } from "@workspace/common/src/helpers/date_helper.js";
import { CREATE_OFFER_ELC } from "./constants.js";
import CreatorsListModal from "../CreatorsListModal/CreatorsListModal.jsx";
import { format, isValid, parse } from "date-fns";

function ELCTable({ status, setStatus, selectedDateRange }) {
  const dispatch = useDispatch();

  const form = useSelector((state) => state?.JobFormReducer?.form);

  const [elcFormOpen, setElcFormOpen] = useState(false);
  const [elcModalOpen, setElcModalOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [tableConfig, setTableConfig] = useState(null);
  const [modalForm, setModalForm] = useState(null);
  const [formTemplate, setFormTemplate] = useState(null);
  const [showTOS, setShowTOS] = useState(false);
  const [creatorsListModalOpen, setCreatorsListModalOpen] = useState(false);

  //Preview and download
  const [previewModal, setPreviewModal] = useState(false);
  const [offerPDFFile, setOfferPDFFile] = useState(null);

  const candidatesData = useSelector(
    (state) => state.PreOfferCandidateReducer.candidates
  );
  const changeStatusMeta = useSelector(
    (state) => state.PreOfferCandidateReducer.changeStatusMeta
  );
  const offerStatusMeta = useSelector(
    (state) => state.PreOfferCandidateReducer.offerStatusMeta
  );

  const jobTagMeta = useSelector((state) => state.JobStageReducer.jobTagMeta);

  const hasPageRendered = useRef(false);

  const formateDate = (rawDate) => {
    let formattedDate = "";
    const parsedDate = parse(rawDate, "dd-MM-yyyy", new Date());
    if (isValid(parsedDate)) {
      formattedDate = format(parsedDate, "yyyy-MM-dd");
    }
    return formattedDate;
  };

  useEffect(() => {
    if (modalForm) {
      dispatch(fetchJobForm(modalForm));
    }
  }, [modalForm]);

  useEffect(() => {
    if (form) {
      setFormTemplate(form);
    }
  }, [form]);

  useEffect(() => {
    if (!elcModalOpen) {
      dispatch(fetchJobFormReset());
      setFormTemplate(null);
      setModalForm(null);
    }
  }, [elcModalOpen]);

  const handleButtonClick = (data) => {
    setSelectedCandidate(data);

    const contractEndDate = data?.contractEndDate;
    if (contractEndDate) {
      const date = isBackDate(contractEndDate);
      if (date) {
        // Renew Contract"
        setModalForm("extend_contract");
        setElcModalOpen(true);
      } else {
        // End Contract
        setModalForm("end_contract");
        setElcModalOpen(true);
      }
      return;
    }

    switch (data?.status) {
      case "OFFER_SHARED":
        setModalForm("offer_acceptance");
        setElcModalOpen(true);
        break;
      case "OFFER_ACCEPTED":
      case "BGC_IN_PROGRESS":
      case "BGC_REJECTED":
      case "BGC_COMPLETED":
      case "MOM_BGC_IN_PROGRESS":
      case "MOM_BGC_COMPLETED":
      case "SC_IN_PROGRESS":
      case "SC_REJECTED":
      case "SC_COMPLETED":
      case "VISA_IN_PROGRESS":
        let formName = "";
        if (data?.hasBGC && !data?.bgcDone) {
          formName = "bgc_status_update";
        } else if (data?.hasMOMBGC && !data?.momBGCDone) {
          formName = "mom_bgc_status_update";
        } else if (data?.hasSC && !data?.scDone) {
          formName = "security_clearance_update";
        } else if (data?.hasVisa && !data?.visaDone) {
          formName = "visa_update";
        }
        setModalForm(formName);
        setElcModalOpen(true);
        break;
      case "ONBOARDING_DOCUMENTS_UPLOADED":
        setModalForm("onboarding_date");
        setElcModalOpen(true);
        break;
      default:
        setShowTOS(false);
        setElcFormOpen(true);
        break;
    }
  };

  // Download Offer
  const downloadOffer = async (selectedCandidate) => {
    try {
      const res = await CommonBackendHelper.downloadDocumentByEntityAndId({
        entityId: selectedCandidate?.id,
        entityType: CREATE_OFFER_ELC,
      });
      const documentData = res.data;

      if (!documentData || !documentData.encodedFile) {
        toast.error("Offer could not be found!");
        return;
      } else {
        FileHelper.downloadBase64File(
          documentData.encodedFile,
          documentData.fileName
        );
        return;
      }
    } catch (e) {
      toast.error("Failed to download offer!");
    }
  };

  // Preview Offer
  const previewOffer = async (selectedCandidate) => {
    try {
      setSelectedCandidate(selectedCandidate);

      const res = await CommonBackendHelper.downloadDocumentByEntityAndId({
        entityId: selectedCandidate?.id,
        entityType: CREATE_OFFER_ELC,
      });
      const documentData = res.data;

      if (!documentData || !documentData.encodedFile) {
        toast.error("Offer could not be found!");
        return;
      } else {
        const file = FileHelper.base64ToFile(
          documentData?.encodedFile,
          documentData?.fileName
        );
        const url = URL.createObjectURL(file);
        setOfferPDFFile(url);
        setPreviewModal(true);
      }
    } catch (e) {
      toast.error("Failed to preview offer.");
    }
  };

  // Edit TOS
  const editTOS = async (selectedCandidate) => {
    setSelectedCandidate(selectedCandidate);
    setElcFormOpen(true);
    setShowTOS(true);
  };

  // Open Creators List
  const openCreatorsList = (selectedCandidate) => {
    setSelectedCandidate(selectedCandidate);
    setCreatorsListModalOpen(true);
  };

  const getSubStatus = (status) => {
    return (
      <>
        {BGC_SUB_STATUS[status] ? (
          <div>
            <span style={{ color: "#2E90FA" }}>●</span> {BGC_SUB_STATUS[status]}
          </div>
        ) : (
          status
        )}
      </>
    );
  };

  const customRenderList = [
    {
      names: ["updatedAt", "createdAt", "joiningDate", "contractEndDate"],
      render: (data, opt) =>
        DateHelper.formatDateStandard2(
          DynamicTableHelper.getDynamicNestedResult(data, opt.value) || "-"
        ),
    },
    {
      names: ["status"],
      render: (data) => (
        <div>
          <span style={{ color: "#2E90FA" }}>●</span>{" "}
          {BGC_STATUS[data?.status] ?? "N/A"}
        </div>
      ),
    },
    {
      names: ["subStatus"],
      render: (data) => getSubStatus(data?.subStatus),
    },
  ];

  const generateReportConfig = (customConfig) => {
    return [
      {
        header: "#",
        name: "indexing",
        sort: false,
        render: (data, index) => (
          <div className="d-flex column-gap-2">
            {pageInfo?.currentPage * pageInfo?.pageSize + (index + 1)}.
          </div>
        ),
      },
      {
        header: "Case No.",
        name: "data.caseNo",
        sort: true,
        sortValue: "pre_offer_verification.case_no",
        expand: true,
        render: (data) => (
          <div className="d-flex gap-1">
            {data?.createOfferId !== -1 && (
              <>
                <TooltipWrapper tooltipText={"Download Offer"}>
                  <div
                    className="cursor-pointer"
                    style={{ color: "#0A56AE" }}
                    onClick={() => downloadOffer(data)}
                  >
                    <span className="mdi mdi-download-circle"></span>
                  </div>
                </TooltipWrapper>
                <TooltipWrapper tooltipText={"Preview Offer"}>
                  <div
                    className="d-flex cursor-pointer"
                    style={{ color: "#0A56AE" }}
                    onClick={() => previewOffer(data)}
                  >
                    <span className="mdi mdi-eye-circle"></span>
                  </div>
                </TooltipWrapper>
              </>
            )}
            <TooltipWrapper tooltipText={"Edit TOS"}>
              <div
                className="d-flex cursor-pointer"
                style={{ color: "#0A56AE" }}
                onClick={() => editTOS(data)}
              >
                <span className="mdi mdi-pencil-circle"></span>
              </div>
            </TooltipWrapper>
            {data?.createOfferId !== -1 && (
              <TooltipWrapper tooltipText={"Creators List"}>
                <div
                  className="d-flex cursor-pointer"
                  style={{ color: "#0A56AE" }}
                  onClick={() => openCreatorsList(data)}
                >
                  <span className="mdi mdi-view-list"></span>
                </div>
              </TooltipWrapper>
            )}
            <strong>{data.caseNo}</strong>
          </div>
        ),
      },
      {
        header: "Pulse JobId",
        name: "data.jobEnity.jobSubmissionData.jobId",
        sort: true,
        sortValue: "j.job_submission_data->>'jobId'",
        expand: true,
        render: (data) => <div>{data.jobEnity.jobSubmissionData.jobId}</div>,
      },
      {
        header: "Job Title",
        name: "data.jobEnity.jobSubmissionData.jobTitle",
        sort: true,
        sortValue: "j.job_submission_data->>'jobTitle'",
        expand: true,
        render: (data) => (
          <Link
            to={`/jobs/${data.jobEnity.id}/snapshot`}
            className="fw-semibold"
            style={{ color: "#0A56AE" }}
          >
            <span>{data.jobEnity.jobSubmissionData.jobTitle}</span>
          </Link>
        ),
      },
      {
        header: "Candidate Name",
        name: "data.candidate.firstName",
        sort: true,
        sortValue: "c.first_name",
        expand: true,
        render: (data) => (
          <Link
            to={`/candidates/${data.candidate.id}/snapshot`}
            className="fw-semibold"
            style={{ color: "#0A56AE" }}
          >
            <span>
              {data.candidate.firstName} {data.candidate?.lastName}
            </span>
          </Link>
        ),
      },
      ...customConfig,
      {
        header: "Next Actions",
        name: "actions",
        sort: false,
        sortValue: "right",
        sticky: "right",
        expand: true,
        center: true,
        render: (data) => {
          const elcStatus = getButtonName(data);
          // Check if elcStatus is not undefined
          const elcStatusExists = elcStatus !== undefined;
          return (
            <>
              {elcStatusExists ? (
                <div className="d-flex flex-row align-items-center gap-2">
                  <div
                    className="next-action-button"
                    onClick={() => handleButtonClick(data)}
                  >
                    {elcStatus}
                  </div>
                  <div className="action-button">
                    <span className="mdi mdi-dots-vertical"></span>
                  </div>
                </div>
              ) : (
                <div className="d-flex flex-row align-items-center gap-2">
                  <div className="next-action-button-disabled">
                    {data?.status === "OFFER_REJECTED"
                      ? "Offer Rejected"
                      : "No action available."}
                  </div>
                  <div className="action-button">
                    <span className="mdi mdi-dots-vertical"></span>
                  </div>
                </div>
              )}
            </>
          );
        },
      },
    ];
  };

  const {
    pageRequest,
    pageRequestSet,
    pageInfo,
    setPageInfoData,
    search,
    setSearch,
    customConfig,
    setCustomConfigData,
    setTableData,
    tableData,
    activeRow,
    setActiveRow,
  } = useTableHook(
    {
      page: 0,
      pageSize: 50,
      sortBy: null,
      sortDirection: "asc",
      searchTerm: null,
      searchFields: SEARCH_FIELDS,
      allActive: status ?? "all",
      dateFrom: null,
      dateTo: null,
    },
    REPORT_MANDATORY_OPTIONS,
    PREOFFER_INITIAL_OPTIONS,
    customRenderList,
    generateReportConfig
  );

  useEffect(() => {
    if (pageRequest) {
      setActiveRow([]);
      dispatch(
        fetchPreOfferCandidates(
          DynamicTableHelper.cleanPageRequest({
            ...pageRequest,
          })
        )
      );
    }
  }, [pageRequest]);

  useEffect(() => {
    if (status) {
      setActiveRow([]);
      if (selectedDateRange?.from && selectedDateRange?.to) {
        const dateFrom = formateDate(selectedDateRange?.from, "yyyy-MM-dd");
        const dateTo = formateDate(selectedDateRange?.to, "yyyy-MM-dd");
        dispatch(
          fetchPreOfferCandidates(
            DynamicTableHelper.cleanPageRequest({
              ...pageRequest,
              allActive: status,
              dateFrom,
              dateTo,
            })
          )
        );
      } else {
        dispatch(
          fetchPreOfferCandidates(
            DynamicTableHelper.cleanPageRequest({
              ...pageRequest,
              allActive: status,
            })
          )
        );
      }
    }
  }, [status]);

  useEffect(() => {
    if (selectedDateRange?.from && selectedDateRange?.to) {
      setActiveRow([]);
      const dateFrom = formateDate(selectedDateRange?.from, "yyyy-MM-dd");
      const dateTo = formateDate(selectedDateRange?.to, "yyyy-MM-dd");
      dispatch(fetchPreOfferCounts({ dateFrom, dateTo }));
      dispatch(fetchSLACounts({ dateFrom, dateTo }));
      dispatch(fetchBGCCounts({ dateFrom, dateTo }));
      dispatch(
        fetchPreOfferCandidates(
          DynamicTableHelper.cleanPageRequest({
            ...pageRequest,
            allActive: status,
            dateFrom,
            dateTo,
          })
        )
      );
    } else {
      dispatch(fetchPreOfferCounts({ dateFrom: null, dateTo: null }));
      dispatch(fetchSLACounts({ dateFrom: null, dateTo: null }));
      dispatch(fetchBGCCounts({ dateFrom: null, dateTo: null }));
      dispatch(
        fetchPreOfferCandidates(
          DynamicTableHelper.cleanPageRequest({
            ...pageRequest,
            allActive: status,
          })
        )
      );
    }
  }, [selectedDateRange]);

  useEffect(() => {
    if (
      changeStatusMeta?.isSuccess ||
      jobTagMeta?.isSuccess ||
      offerStatusMeta?.isSuccess
    ) {
      dispatch(resetChangeStatus());
      dispatch(resetOfferStatus());
      dispatch(resetOfferWithAttachmentsStatus());
      dispatch(tagReset());
      dispatch(
        fetchPreOfferCandidates(
          DynamicTableHelper.cleanPageRequest({
            ...pageRequest,
            allActive: "all",
          })
        )
      );
      dispatch(fetchPreOfferCounts({ dateFrom: null, dateTo: null }));
      dispatch(fetchSLACounts({ dateFrom: null, dateTo: null }));
      dispatch(fetchBGCCounts({ dateFrom: null, dateTo: null }));
      setElcFormOpen(false);
      setElcModalOpen(false);
      setShowTOS(false);
    }
  }, [
    changeStatusMeta?.isSuccess,
    dispatch,
    jobTagMeta?.isSuccess,
    offerStatusMeta?.isSuccess,
    pageRequest,
  ]);

  // Update the page info when candidate Data changes
  useEffect(() => {
    if (candidatesData) {
      setPageInfoData(candidatesData);
      setTableData(candidatesData?.candidates);
    }
  }, [candidatesData]);

  useEffect(() => {
    if (tableConfig) {
      const newConfig = generateReportConfig(customConfig);
      newConfig.forEach((item, index) => {
        const oldConfig = tableConfig.find(
          (oldItem) => oldItem?.name === item?.name
        );
        if (!oldConfig) return;
        if (oldConfig?.expand) {
          newConfig[index].expand = true;
        } else {
          newConfig[index].expand = false;
        }
      });
      setTableConfig(newConfig);
    } else {
      setTableConfig(generateReportConfig(customConfig));
    }
  }, [customConfig, pageInfo, activeRow, tableData]);

  useEffect(() => {
    if (hasPageRendered.current) {
      pageRequestSet.setPage(0);
    }
    hasPageRendered.current = true;
  }, []);

  const getButtonName = (data) => {
    const status = data?.status;
    const contractEndDate = data?.contractEndDate;
    if (contractEndDate && data?.status !== "CONTRACT_END") {
      const date = isBackDate(contractEndDate);
      if (date) {
        return "Renew Contract";
      } else {
        return "End Contract";
      }
    }
    switch (status) {
      case "COMPLETED":
        return "Verify Pre-Offer";
      case "VERIFIED":
      case "CREATE_OFFER_DRAFT":
        return "Create Offer";
      case "OFFER_RENEGOTIATION":
        return "Recreate Offer";
      case "REJECTED":
        return "Reverify Pre-Offer";
      case "CREATE_OFFER_SUBMITTED":
        return "Review Offer";
      case "OFFER_REVIEWED":
        return "Approve Offer";
      case "CORRECTION_NEEDED":
        return "Edit Offer";
      case "OFFER_APPROVED":
        return "Send Offer";
      case "OFFER_SHARED":
        return "Offer Acceptance";
      case "OFFER_ACCEPTED":
      case "BGC_IN_PROGRESS":
      case "BGC_REJECTED":
      case "BGC_COMPLETED":
      case "MOM_BGC_IN_PROGRESS":
      case "MOM_BGC_COMPLETED":
      case "SC_IN_PROGRESS":
      case "SC_REJECTED":
      case "SC_COMPLETED":
      case "VISA_IN_PROGRESS":
      case "VISA_COMPLETED":
        return "Status Update";
      case "ONBOARDING_PENDING":
        return "Upload Onboarding";
      case "ONBOARDING_DOCUMENTS_UPLOADED":
        return "Onboarding";
      default:
        break;
    }
  };

  return (
    <React.Fragment>
      <div className="elc-offcanvas">
        <DynamicTableWrapper
          data={candidatesData.preOffers}
          config={tableConfig}
          pageInfo={pageInfo}
          pageRequest={pageRequest}
          pageRequestSet={pageRequestSet}
          search={search}
          setSearch={setSearch}
          setStatus={setStatus}
          setCustomConfigData={setCustomConfigData}
          header="Offers"
          activeRow={activeRow}
          setActiveRow={setActiveRow}
          setTableConfig={setTableConfig}
        />
      </div>
      <ELCForms
        selectedCandidate={selectedCandidate}
        showTOS={showTOS}
        setShowTOS={setShowTOS}
        isOpen={elcFormOpen}
        setElcFormOpen={setElcFormOpen}
        setElcModalOpen={setElcModalOpen}
        setModalForm={setModalForm}
      />
      <ELCModal
        selectedCandidate={selectedCandidate}
        elcModalOpen={elcModalOpen}
        setElcModalOpen={setElcModalOpen}
        modalForm={modalForm}
        formTemplate={formTemplate}
      />
      <CreatorsListModal
        isOpen={creatorsListModalOpen}
        setIsOpen={setCreatorsListModalOpen}
        selectedCandidate={selectedCandidate}
      />
      <OfferPreviewModal
        isOpen={previewModal}
        toggle={() => setPreviewModal(!previewModal)}
        selectedCandidate={selectedCandidate}
        offerPDFFile={offerPDFFile}
      />
    </React.Fragment>
  );
}

export default ELCTable;
